// @ts-check

import { atom, useSetAtom } from "jotai";
import { useChatContext } from ".";

const ChatUploadTypes = /**@type{const}*/({
  IMAGE: "image",
  FILE: "file",
})

/** @typedef {typeof ChatUploadTypes[keyof typeof ChatUploadTypes]} ChatUploadType */

/**
 * @typedef {Object} ImageUploadRecord
 * @property {Extract<ChatUploadType, "image">} type
 * @property {File} file
 * @property {AbortController} controller
 * @property {Promise<ChatImage>} promise
 */
/**
 * @typedef {Object} FileUploadRecord
 * @property {Extract<ChatUploadType, "file">} type
 * @property {File} file
 * @property {AbortController} controller
 * @property {Promise<string>} promise
 */

/** @typedef {ImageUploadRecord | FileUploadRecord} UploadRecord */

const uploadsStoreAtom = atom(/** @type {Map<File, UploadRecord>}*/(new Map()))

function useChatUploadsManager() {
  const setUploadsStore = useSetAtom(uploadsStoreAtom)
  const { useUploadChatSessionImage, useUploadFile } = useChatContext()
  const { mutateAsync: uploadChatImage } = useUploadChatSessionImage()
  const { mutateAsync: uploadFile } = useUploadFile()

  /**
   * @template {ChatUploadType} T
   * @param {T} type
   * @param {File} file
   */
  function startUpload(type, file) {
    const uploadController = new AbortController()
    const uploadPromise = new Promise((resolve, reject) => {
      uploadController.signal.addEventListener("abort", () => {
        reject(uploadController.signal.reason)
      })

      if (type === ChatUploadTypes.IMAGE) {
        uploadChatImage({ file }).then(resolve, reject)
      } else if (type === ChatUploadTypes.FILE) {
        uploadFile({ file }).then(resolve, reject)
      } else {
        reject(new Error(`Unknown upload type: ${type}`))
      }
    })

    uploadPromise
      .catch(() => { /* To prevent uncaught rejection */ })
      .finally(() => {
        setUploadsStore(prev => {
          const newStore = new Map(prev)
          newStore.delete(file)
          return newStore
        })
      })

    setUploadsStore(prev => new Map(prev).set(file, {
      type,
      file,
      controller: uploadController,
      promise: uploadPromise,
    }))

    return /** @type {T extends Extract<ChatUploadType, "image"> ? Promise<ChatImage> : T extends Extract<ChatUploadType, "file"> ? Promise<string> : never} */(uploadPromise)
  }

  /**
   * @param {File} file
   */
  function cancelUpload(file) {
    setUploadsStore(prev => {
      const record = prev.get(file)
      if (record == null) return prev
      record.controller.abort("Upload cancelled")
      return prev
    })
  }

  return { startUpload, cancelUpload }
}

export {
  ChatUploadTypes,
  useChatUploadsManager,
}